import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
    ArrowLeft,
    CheckCircle2,
    ClipboardList,
    Plus,
    RefreshCw,
    Save,
    UserPlus,
    Users,
} from "lucide-react";
import { API_BASE_URL, getRequest, postRequest, putRequest } from "../../../../api";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import "./RecruitmentCandidateEvaluationPage.css";

const ROLE_LABELS = {
    MANAGER: "Manager",
    COLLABORATOR: "Collaborateur",
    RESPONSABLE_FORMATION: "Responsable formation",
    ALL: "Tous",
};

const STATUS_LABELS = {
    EN_ATTENTE: "En attente",
    EN_COURS: "En cours",
    TERMINE: "Terminé",
    ANNULE: "Annulé",
};

const DECISION_LABELS = {
    VALIDE: "Validé",
    REFUSE: "Refusé",
    A_REVOIR: "À revoir",
};

const buildAnswersMap = (answers) =>
    (answers || []).reduce((acc, item) => {
        acc[item.questionId] = {
            valeurTexte: item.valeurTexte || "",
            valeurNumerique: item.valeurNumerique ?? "",
            valeurScaleOptionId: item.valeurScaleOptionId ?? "",
            commentaire: item.commentaire || "",
            scoreCalcule: item.scoreCalcule ?? null,
        };
        return acc;
    }, {});

const buildIntervenantsForm = (fiche) =>
    (fiche?.intervenants || []).map((item, index) => ({
        intervenantId: item.intervenantId,
        ordre: item.ordre ?? index + 1,
        estResponsable: !!item.estResponsable,
    }));

const RecruitmentCandidateEvaluationPage = ({ viewMode = "RH" }) => {
    const { evaluationId, candidatureId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const users = useSelector((state) => state.user?.users || []);
    const currentUser = useSelector((state) => state.user?.currentUser);
    const effectiveViewMode = location.state?.viewMode || viewMode;
    const isManagerView = effectiveViewMode === "MANAGER";
    const isRhView = effectiveViewMode === "RH";

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fiche, setFiche] = useState(null);
    const [answersMap, setAnswersMap] = useState({});
    const [commentaireGlobal, setCommentaireGlobal] = useState("");
    const [intervenantsForm, setIntervenantsForm] = useState([]);
    const [selectedIntervenantInstanceId, setSelectedIntervenantInstanceId] = useState(null);
    const [openIntervenantPickerIndex, setOpenIntervenantPickerIndex] = useState(null);

    useEffect(() => {
        if (!users.length) {
            dispatch(getAllUsersRequest());
        }
    }, [dispatch, users.length]);

    const fetchFiche = async () => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem("token");
            const url = evaluationId
                ? `${API_BASE_URL}/api/recruitment-evaluations/evaluations/${evaluationId}/candidatures/${candidatureId}/fiche`
                : `${API_BASE_URL}/api/recruitment-evaluations/candidatures/${candidatureId}/fiche`;
            const resp = await getRequest({ url, token });
            setFiche(resp.data);
            setAnswersMap(buildAnswersMap(resp.data.reponsesCourantes));
            setCommentaireGlobal(resp.data.intervenantCourant?.commentaireGlobalIntervenant || "");
            setIntervenantsForm(buildIntervenantsForm(resp.data));
            setSelectedIntervenantInstanceId(
                resp.data.intervenantCourant?.id ||
                resp.data.intervenants?.find((item) => item.statut !== "ANNULE")?.id ||
                null
            );
        } catch (error) {
            Swal.fire({
                icon: "error",
                text: error?.response?.data?.message || "Impossible de charger la fiche d'évaluation",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiche();
    }, [evaluationId, candidatureId]);

    const scaleOptions = useMemo(
        () => [...(fiche?.evaluation?.scaleOptions || [])].sort((a, b) => (a.position || 0) - (b.position || 0)),
        [fiche]
    );

    const sections = useMemo(
        () => [...(fiche?.evaluation?.sections || [])].sort((a, b) => (a.position || 0) - (b.position || 0)),
        [fiche]
    );

    const canManageIntervenants = !!fiche?.gestionIntervenantsAutorisee;
    const canEditCurrent = !!fiche?.intervenantCourant?.editionAutorisee;
    const isCurrentEvaluationTerminated = fiche?.intervenantCourant?.statut === "TERMINE";
    const maxIntervenants = fiche?.evaluation?.settings?.maxIntervenants ?? null;
    const canAddIntervenant = maxIntervenants == null || intervenantsForm.length < maxIntervenants;
    const recommendedManagerId = fiche?.candidature?.demandeurId ?? null;

    const getUserLabel = (user) =>
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.fullName || user?.username || "Utilisateur";

    const orderedUsers = useMemo(() => {
        const list = [...users];
        if (!recommendedManagerId) {
            return list.sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b)));
        }
        return list.sort((a, b) => {
            const aRecommended = Number(a.id) === Number(recommendedManagerId);
            const bRecommended = Number(b.id) === Number(recommendedManagerId);
            if (aRecommended && !bRecommended) return -1;
            if (!aRecommended && bRecommended) return 1;
            return getUserLabel(a).localeCompare(getUserLabel(b));
        });
    }, [users, recommendedManagerId]);

    const displayedIntervenant = useMemo(() => {
        if (!isRhView) return fiche?.intervenantCourant || null;
        return (
            (fiche?.intervenants || []).find((item) => Number(item.id) === Number(selectedIntervenantInstanceId)) ||
            (fiche?.intervenants || []).find((item) => item.statut !== "ANNULE") ||
            null
        );
    }, [fiche, isRhView, selectedIntervenantInstanceId]);

    const canEditDisplayed = !isRhView && canEditCurrent;

    useEffect(() => {
        const reponses = isRhView ? displayedIntervenant?.reponses : fiche?.reponsesCourantes;
        setAnswersMap(buildAnswersMap(reponses));
        setCommentaireGlobal(displayedIntervenant?.commentaireGlobalIntervenant || "");
    }, [displayedIntervenant, fiche?.reponsesCourantes, isRhView]);

    const updateAnswerValue = (questionId, field, value) => {
        setAnswersMap((prev) => ({
            ...prev,
            [questionId]: {
                valeurTexte: "",
                valeurNumerique: "",
                valeurScaleOptionId: "",
                commentaire: "",
                ...(prev[questionId] || {}),
                [field]: value,
            },
        }));
    };

    const serializeAnswers = () =>
        Object.entries(answersMap).map(([questionId, payload]) => ({
            questionId: Number(questionId),
            valeurTexte: payload.valeurTexte || null,
            valeurNumerique: payload.valeurNumerique === "" ? null : Number(payload.valeurNumerique),
            valeurScaleOptionId: payload.valeurScaleOptionId === "" ? null : Number(payload.valeurScaleOptionId),
            commentaire: payload.commentaire || null,
        }));

    const saveCurrentEvaluation = async (mode) => {
        if (!fiche?.id || !fiche?.intervenantCourant?.id) return;
        setSaving(true);
        try {
            const token = sessionStorage.getItem("token");
            const url =
                mode === "draft"
                    ? `${API_BASE_URL}/api/recruitment-evaluations/instances/${fiche.id}/intervenants/${fiche.intervenantCourant.id}/brouillon`
                    : `${API_BASE_URL}/api/recruitment-evaluations/instances/${fiche.id}/intervenants/${fiche.intervenantCourant.id}/terminer`;
            const resp = await putRequest({
                url,
                token,
                data: {
                    commentaireGlobalIntervenant: commentaireGlobal,
                    reponses: serializeAnswers(),
                },
            });
            setFiche(resp.data);
            setAnswersMap(buildAnswersMap(resp.data.reponsesCourantes));
            setCommentaireGlobal(resp.data.intervenantCourant?.commentaireGlobalIntervenant || "");
            setSelectedIntervenantInstanceId(
                resp.data.intervenantCourant?.id ||
                resp.data.intervenants?.find((item) => item.statut !== "ANNULE")?.id ||
                null
            );
            Swal.fire({
                icon: "success",
                timer: 1800,
                showConfirmButton: false,
                text: mode === "draft" ? "Brouillon enregistré avec succès" : "Évaluation terminée avec succès",
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                text: error?.response?.data?.message || "Impossible d'enregistrer l'évaluation",
            });
        } finally {
            setSaving(false);
        }
    };

    const saveIntervenants = async () => {
        if (!fiche?.id) return;
        setSaving(true);
        try {
            const token = sessionStorage.getItem("token");
            const resp = await putRequest({
                url: `${API_BASE_URL}/api/recruitment-evaluations/instances/${fiche.id}/intervenants`,
                token,
                data: {
                    intervenants: intervenantsForm
                        .filter((item) => item.intervenantId)
                        .map((item, index) => ({
                            intervenantId: Number(item.intervenantId),
                            ordre: Number(item.ordre || index + 1),
                            estResponsable: !!item.estResponsable,
                        })),
                },
            });
            setFiche(resp.data);
            setIntervenantsForm(buildIntervenantsForm(resp.data));
            Swal.fire({
                icon: "success",
                timer: 1800,
                showConfirmButton: false,
                text: "Liste des intervenants mise à jour",
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                text: error?.response?.data?.message || "Impossible de mettre à jour les intervenants",
            });
        } finally {
            setSaving(false);
        }
    };

    const recalculateSynthese = async () => {
        if (!fiche?.id) return;
        setSaving(true);
        try {
            const token = sessionStorage.getItem("token");
            const resp = await postRequest({
                url: `${API_BASE_URL}/api/recruitment-evaluations/instances/${fiche.id}/recalculer`,
                token,
                data: {},
            });
            setFiche(resp.data);
            Swal.fire({
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
                text: "Synthèse recalculée",
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                text: error?.response?.data?.message || "Impossible de recalculer la synthèse",
            });
        } finally {
            setSaving(false);
        }
    };

    const updateIntervenantRow = (index, field, value) => {
        setIntervenantsForm((prev) =>
            prev.map((row, rowIndex) =>
                rowIndex === index
                    ? { ...row, [field]: value }
                    : field === "estResponsable" && value
                        ? { ...row, estResponsable: false }
                        : row
            )
        );
    };

    const addIntervenantRow = () => {
        setIntervenantsForm((prev) => [
            ...prev,
            { intervenantId: "", ordre: prev.length + 1, estResponsable: false },
        ]);
    };

    const removeIntervenantRow = (index) => {
        setIntervenantsForm((prev) =>
            prev
                .filter((_, rowIndex) => rowIndex !== index)
                .map((row, rowIndex) => ({ ...row, ordre: rowIndex + 1 }))
        );
        setOpenIntervenantPickerIndex(null);
    };

    const renderIntervenantUserPicker = (item, index) => {
        const selectedUser = users.find((user) => Number(user.id) === Number(item.intervenantId));
        const isOpen = openIntervenantPickerIndex === index;

        return (
            <div className="rcp-user-picker">
                <button
                    className="rcp-user-picker-button"
                    type="button"
                    disabled={saving}
                    onClick={() => setOpenIntervenantPickerIndex(isOpen ? null : index)}
                >
                    <span>{selectedUser ? getUserLabel(selectedUser) : "Sélectionner un intervenant"}</span>
                    {selectedUser && Number(selectedUser.id) === Number(recommendedManagerId) && (
                        <span className="rcp-recommended-badge">recommandé</span>
                    )}
                </button>
                {isOpen && (
                    <div className="rcp-user-picker-menu">
                        {orderedUsers.map((user) => {
                            const isRecommended = Number(user.id) === Number(recommendedManagerId);
                            const isSelected = Number(user.id) === Number(item.intervenantId);
                            return (
                                <button
                                    key={user.id}
                                    className={`rcp-user-picker-option ${isSelected ? "rcp-user-picker-option--selected" : ""}`}
                                    type="button"
                                    onClick={() => {
                                        updateIntervenantRow(index, "intervenantId", user.id);
                                        setOpenIntervenantPickerIndex(null);
                                    }}
                                >
                                    <span>{getUserLabel(user)}</span>
                                    {isRecommended && <span className="rcp-recommended-badge">recommandé</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderQuestionInput = (question) => {
        const answer = answersMap[question.id] || {};
        const baseDisabled = !canEditDisplayed || saving;
        const selectedScaleOptionId =
            answer.valeurScaleOptionId ||
            scaleOptions.find((option) => Number(option.value) === Number(answer.scoreCalcule))?.id ||
            "";

        if (question.questionType === "TEXT" || question.questionType === "DISSERTATION") {
            return (
                <textarea
                    className="rcp-answer-textarea"
                    rows={3}
                    value={answer.valeurTexte || ""}
                    disabled={baseDisabled}
                    onChange={(e) => updateAnswerValue(question.id, "valeurTexte", e.target.value)}
                    placeholder="Saisir votre réponse"
                />
            );
        }

        if (question.questionType === "BOOLEAN" || question.questionType === "OUI_NON") {
            return (
                <div className="rcp-inline-options">
                    <label>
                        <input
                            type="radio"
                            disabled={baseDisabled}
                            checked={String(answer.valeurTexte || "") === "true"}
                            onChange={() => updateAnswerValue(question.id, "valeurTexte", "true")}
                        />
                        Oui
                    </label>
                    <label>
                        <input
                            type="radio"
                            disabled={baseDisabled}
                            checked={String(answer.valeurTexte || "") === "false"}
                            onChange={() => updateAnswerValue(question.id, "valeurTexte", "false")}
                        />
                        Non
                    </label>
                </div>
            );
        }

        if (question.questionType === "CHOICE") {
            return (
                <select
                    className="rcp-select"
                    value={selectedScaleOptionId}
                    disabled={baseDisabled}
                    onChange={(e) => updateAnswerValue(question.id, "valeurScaleOptionId", e.target.value)}
                >
                    <option value="">Sélectionner une option</option>
                    {scaleOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.label}
                        </option>
                    ))}
                </select>
            );
        }

        return (
            <div className="rcp-rating-grid">
                {scaleOptions.map((option) => (
                    <label key={option.id} className="rcp-rating-option">
                        <input
                            type="radio"
                            name={`question-${question.id}`}
                            disabled={baseDisabled}
                            checked={String(selectedScaleOptionId) === String(option.id)}
                            onChange={() => updateAnswerValue(question.id, "valeurScaleOptionId", option.id)}
                        />
                        <span>{option.label}</span>
                    </label>
                ))}
            </div>
        );
    };

    /* ── Loading / empty states ─────────────────────────── */
    if (loading) {
        return (
            <div className="rcp-page">
                <div className="rcp-loading">Chargement de la fiche d'évaluation…</div>
            </div>
        );
    }

    if (!fiche) {
        return (
            <div className="rcp-page">
                <div className="rcp-loading">Fiche indisponible.</div>
            </div>
        );
    }

    /* ================================================================
       MANAGER VIEW
       Layout: header → [candidat | poste] → questions + footer
    ================================================================ */
    if (isManagerView) {
        return (
            <div className="rcp-page rcp-page--manager">
                {/* Header */}
                <div className="rcp-header">
                    <div>
                        <button className="rcp-back" type="button" onClick={() => navigate(-1)}>
                            <ArrowLeft size={15} />
                            Retour
                        </button>
                        <h1>Fiche d'évaluation candidat</h1>
                        <p>
                            {fiche.evaluation?.name} — {fiche.candidature?.candidatNomComplet}
                        </p>
                    </div>
                    <div className="rcp-header-actions">
                        <button
                            className="rcp-btn rcp-btn-secondary"
                            type="button"
                            onClick={fetchFiche}
                            disabled={saving}
                        >
                            <RefreshCw size={15} />
                            Actualiser
                        </button>
                    </div>
                </div>

                {/* Info cards */}
                <div className="rcp-top-grid rcp-top-grid--manager">
                    <section className="rcp-card">
                        <h2>Candidat</h2>
                        <div className="rcp-meta-grid">
                            <div><strong>Nom complet :</strong> {fiche.candidature?.candidatNomComplet || "—"}</div>
                            <div><strong>Email :</strong> {fiche.candidature?.candidatEmail || "—"}</div>
                            <div><strong>Téléphone :</strong> {fiche.candidature?.candidatPhone || "—"}</div>
                            <div><strong>Titre :</strong> {fiche.candidature?.candidatTitreProfessionnel || "—"}</div>
                        </div>
                    </section>

                    <section className="rcp-card">
                        <h2>Poste</h2>
                        <div className="rcp-meta-grid">
                            <div><strong>Poste :</strong> {fiche.candidature?.autrePoste || fiche.candidature?.posteNom || "—"}</div>
                            <div><strong>Établissement :</strong> {fiche.candidature?.autreEtablissement || fiche.candidature?.etablissementNom || "—"}</div>
                            <div><strong>Type de demande :</strong> {fiche.candidature?.typeDemande || "—"}</div>
                            <div><strong>Type d'emploi :</strong> {fiche.candidature?.typeEmploi || "—"}</div>
                        </div>
                    </section>
                </div>

                {/* Evaluation form */}
                <section className="rcp-card">
                    <div className="rcp-section-header">
                        <h2><ClipboardList size={16} /> Grille d'évaluation</h2>
                        <div className="rcp-inline-actions">
                            <div className="rcp-current-badge">
                                {fiche.intervenantCourant?.intervenantNom || currentUser?.fullName || "Lecture seule"}
                            </div>
                            <div className="rcp-badge">
                                {STATUS_LABELS[fiche.intervenantCourant?.statut] || "En attente"}
                            </div>
                        </div>
                    </div>

                    {sections.map((section) => (
                        <div key={section.id} className="rcp-section-block">
                            <div className="rcp-section-title">
                                <h3>{section.title}</h3>
                                <span>{section.weight}%</span>
                            </div>
                            {section.description && (
                                <p className="rcp-section-description">{section.description}</p>
                            )}
                            {(section.questions || []).map((question) => (
                                <div key={question.id} className="rcp-question-card">
                                    <div className="rcp-question-header">
                                        <strong>{question.label}</strong>
                                    </div>
                                    {renderQuestionInput(question)}
                                    <textarea
                                        className="rcp-commentaire"
                                        rows={2}
                                        disabled={!canEditDisplayed || saving}
                                        value={answersMap[question.id]?.commentaire || ""}
                                        onChange={(e) =>
                                            updateAnswerValue(question.id, "commentaire", e.target.value)
                                        }
                                        placeholder="Commentaire sur cette réponse"
                                    />
                                </div>
                            ))}
                        </div>
                    ))}

                    {/* Global comment */}
                    <div className="rcp-question-card">
                        <div className="rcp-section-title">
                            <h3>Commentaire global</h3>
                        </div>
                        <textarea
                            className="rcp-answer-textarea"
                            rows={4}
                            disabled={!canEditDisplayed || saving}
                            value={commentaireGlobal}
                            onChange={(e) => setCommentaireGlobal(e.target.value)}
                            placeholder="Saisir votre commentaire global sur le candidat"
                        />
                    </div>

                    {/* Footer actions */}
                    <div className="rcp-footer-actions">
                        <button
                            className="rcp-btn rcp-btn-secondary"
                            type="button"
                            onClick={() => saveCurrentEvaluation("draft")}
                            disabled={!canEditDisplayed || saving}
                        >
                            <Save size={15} />
                            Enregistrer brouillon
                        </button>
                        <button
                            className="rcp-btn rcp-btn-secondary"
                            type="button"
                            onClick={() => saveCurrentEvaluation("draft")}
                            disabled={!canEditDisplayed || saving}
                        >
                            <Save size={15} />
                            Enregistrer
                        </button>
                        <button
                            className="rcp-btn rcp-btn-primary"
                            type="button"
                            onClick={() => saveCurrentEvaluation("submit")}
                            disabled={!canEditDisplayed || isCurrentEvaluationTerminated || saving}
                        >
                            <CheckCircle2 size={15} />
                            {isCurrentEvaluationTerminated ? "Évaluation terminée" : "Terminer mon évaluation"}
                        </button>
                    </div>
                </section>
            </div>
        );
    }

    /* ================================================================
       RH VIEW
       Layout (vertical, full-width like Manager):
         header (+ "Validé" badge if decisionFinale === VALIDE)
         [Candidat | Poste]              ← 2 cols
         Intervenants                    ← full width
         Grille d'évaluation             ← full width
         Synthèse                        ← full width
    ================================================================ */
    const isValide = fiche?.decisionFinale === "VALIDE";

    return (
        <div className="rcp-page rcp-page--rh">
            {/* Header */}
            <div className="rcp-header">
                <div>
                    <button className="rcp-back" type="button" onClick={() => navigate(-1)}>
                        <ArrowLeft size={15} />
                        Retour
                    </button>
                    <div className="rcp-header-title-row">
                        <h1>Fiche d'évaluation candidat</h1>
                        {isValide && (
                            <span className="rcp-badge-valide">
                                <CheckCircle2 size={14} />
                                Validé
                            </span>
                        )}
                    </div>
                    <p>
                        {fiche.evaluation?.name} — {fiche.candidature?.candidatNomComplet}
                    </p>
                </div>
                <div className="rcp-header-actions">
                    <button
                        className="rcp-btn rcp-btn-secondary"
                        type="button"
                        onClick={fetchFiche}
                        disabled={saving}
                    >
                        <RefreshCw size={15} />
                        Actualiser
                    </button>
                    <button
                        className="rcp-btn rcp-btn-secondary"
                        type="button"
                        onClick={recalculateSynthese}
                        disabled={saving}
                    >
                        <ClipboardList size={15} />
                        Recalculer la synthèse
                    </button>
                </div>
            </div>

            {/* [Candidat | Poste] — 2 cols */}
            <div className="rcp-top-grid rcp-top-grid--manager">
                <section className="rcp-card">
                    <h2>Candidat</h2>
                    <div className="rcp-meta-grid">
                        <div><strong>Nom complet :</strong> {fiche.candidature?.candidatNomComplet || "—"}</div>
                        <div><strong>Email :</strong> {fiche.candidature?.candidatEmail || "—"}</div>
                        <div><strong>Téléphone :</strong> {fiche.candidature?.candidatPhone || "—"}</div>
                        <div><strong>Titre :</strong> {fiche.candidature?.candidatTitreProfessionnel || "—"}</div>
                    </div>
                </section>

                <section className="rcp-card">
                    <h2>Poste</h2>
                    <div className="rcp-meta-grid">
                        <div><strong>Poste :</strong> {fiche.candidature?.autrePoste || fiche.candidature?.posteNom || "—"}</div>
                        <div><strong>Établissement :</strong> {fiche.candidature?.autreEtablissement || fiche.candidature?.etablissementNom || "—"}</div>
                        <div><strong>Type de demande :</strong> {fiche.candidature?.typeDemande || "—"}</div>
                        <div><strong>Type d'emploi :</strong> {fiche.candidature?.typeEmploi || "—"}</div>
                    </div>
                </section>
            </div>

            {/* Intervenants — full width */}
            <section className="rcp-card">
                <div className="rcp-section-header">
                    <h2><Users size={16} /> Intervenants</h2>
                    {canManageIntervenants && (
                        <div className="rcp-inline-actions">
                            <button
                                className="rcp-btn rcp-btn-secondary"
                                type="button"
                                onClick={addIntervenantRow}
                                disabled={!canAddIntervenant || saving}
                            >
                                <Plus size={14} />
                                Ajouter
                            </button>
                            <button
                                className="rcp-btn rcp-btn-primary"
                                type="button"
                                onClick={saveIntervenants}
                                disabled={saving}
                            >
                                <UserPlus size={14} />
                                Désigner
                            </button>
                        </div>
                    )}
                </div>

                <div className="rcp-intervenants-list rcp-intervenants-list--inline">
                    {intervenantsForm.map((item, index) => {
                        const assigned = fiche.intervenants?.find(
                            (row) => Number(row.intervenantId) === Number(item.intervenantId)
                        );
                        return (
                            <div
                                key={`${item.intervenantId || "new"}-${index}`}
                                className="rcp-intervenant-row"
                            >
                                {canManageIntervenants ? (
                                    <>
                                        {renderIntervenantUserPicker(item, index)}
                                        <select
                                            className="rcp-select rcp-native-user-select"
                                            value={item.intervenantId || ""}
                                            onChange={(e) =>
                                                updateIntervenantRow(index, "intervenantId", e.target.value)
                                            }
                                            disabled={saving}
                                        >
                                            <option value="">Sélectionner un intervenant</option>
                                            {users.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                                                        user.username}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            className="rcp-input rcp-order"
                                            type="number"
                                            min="1"
                                            value={item.ordre}
                                            onChange={(e) =>
                                                updateIntervenantRow(index, "ordre", e.target.value)
                                            }
                                            disabled={saving}
                                        />
                                        <label className="rcp-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={!!item.estResponsable}
                                                onChange={(e) =>
                                                    updateIntervenantRow(index, "estResponsable", e.target.checked)
                                                }
                                                disabled={saving}
                                            />
                                            Resp.
                                        </label>
                                        <button
                                            className="rcp-remove"
                                            type="button"
                                            onClick={() => removeIntervenantRow(index)}
                                            disabled={saving}
                                        >
                                            Retirer
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="rcp-intervenant-name">
                                            {assigned?.intervenantNom || "Intervenant non affecté"}
                                        </div>
                                        <div className="rcp-badge">
                                            {STATUS_LABELS[assigned?.statut] || "En attente"}
                                        </div>
                                        <div className="rcp-muted">#{assigned?.ordre || item.ordre}</div>
                                        <div className="rcp-muted">
                                            {assigned?.estResponsable ? "Responsable" : "Intervenant"}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    {!intervenantsForm.length && (
                        <div className="rcp-empty">Aucun intervenant désigné pour le moment.</div>
                    )}
                </div>

                {!!fiche.intervenants?.length && (
                    <div className="rcp-history">
                        <h3>Historique</h3>
                        <div className="rcp-history-grid">
                            {fiche.intervenants.map((item) => (
                                <div key={item.id} className="rcp-history-item">
                                    <div>
                                        <strong>{item.intervenantNom}</strong>
                                        <span className="rcp-history-role">
                                            {item.estResponsable ? "Responsable" : "Intervenant"}
                                        </span>
                                    </div>
                                    <div className="rcp-history-meta">
                                        <span>{STATUS_LABELS[item.statut] || item.statut}</span>
                                        <span>#{item.ordre}</span>
                                        <span>
                                            {item.scoreIntervenant != null ? `Score ${item.scoreIntervenant}` : "—"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {/* Grille d'évaluation — full width */}
            <section className="rcp-card">
                <div className="rcp-section-header">
                    <h2><ClipboardList size={16} /> Réponses de l'intervenant</h2>
                    <div className="rcp-inline-actions">
                        {!!fiche.intervenants?.length && (
                            <select
                                className="rcp-select"
                                value={displayedIntervenant?.id || ""}
                                onChange={(e) => setSelectedIntervenantInstanceId(Number(e.target.value))}
                                disabled={saving}
                            >
                                {fiche.intervenants
                                    .filter((item) => item.statut !== "ANNULE")
                                    .map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.intervenantNom}
                                        </option>
                                    ))}
                            </select>
                        )}
                        <div className="rcp-current-badge">
                            {displayedIntervenant?.intervenantNom ||
                                currentUser?.fullName ||
                                "Lecture seule"}
                        </div>
                        <div className="rcp-badge">
                            {STATUS_LABELS[displayedIntervenant?.statut] || "En attente"}
                        </div>
                        {displayedIntervenant?.scoreIntervenant != null && (
                            <div className="rcp-badge">
                                Note {displayedIntervenant.scoreIntervenant}
                            </div>
                        )}
                    </div>
                </div>

                {sections.map((section) => (
                    <div key={section.id} className="rcp-section-block">
                        <div className="rcp-section-title">
                            <h3>{section.title}</h3>
                            <span>{section.weight}%</span>
                        </div>
                        {section.description && (
                            <p className="rcp-section-description">{section.description}</p>
                        )}
                        {(section.questions || []).map((question) => (
                            <div key={question.id} className="rcp-question-card">
                                <div className="rcp-question-header">
                                    <strong>{question.label}</strong>
                                </div>
                                {renderQuestionInput(question)}
                                <textarea
                                    className="rcp-commentaire"
                                    rows={2}
                                    disabled={!canEditDisplayed || saving}
                                    value={answersMap[question.id]?.commentaire || ""}
                                    onChange={(e) =>
                                        updateAnswerValue(question.id, "commentaire", e.target.value)
                                    }
                                    placeholder="Commentaire sur cette réponse"
                                />
                            </div>
                        ))}
                    </div>
                ))}

                {/* Global comment */}
                <div className="rcp-question-card">
                    <div className="rcp-section-title">
                        <h3>Commentaire global de l'intervenant</h3>
                    </div>
                    <textarea
                        className="rcp-answer-textarea"
                        rows={4}
                        disabled={!canEditDisplayed || saving}
                        value={commentaireGlobal}
                        onChange={(e) => setCommentaireGlobal(e.target.value)}
                        placeholder="Commentaire global sur le candidat"
                    />
                </div>
            </section>

            {/* Synthèse — full width */}
            <section className="rcp-card rcp-card--synthese">
                <div className="rcp-section-header">
                    <h2><ClipboardList size={16} /> Synthèse & Décision finale</h2>
                    <div className="rcp-inline-actions">
                        <div className={`rcp-decision-badge rcp-decision-badge--${(fiche.decisionFinale || "pending").toLowerCase()}`}>
                            {DECISION_LABELS[fiche.decisionFinale] || "En attente"}
                        </div>
                        {fiche.seuilAtteint != null && (
                            <div className="rcp-badge">
                                Seuil {fiche.seuilAtteint ? "atteint" : "non atteint"}
                            </div>
                        )}
                    </div>
                </div>
                <div className="rcp-synthese-grid">
                    <div className="rcp-synthese-kpi">
                        <span className="rcp-kpi-label">Statut global</span>
                        <span className="rcp-kpi-value">{fiche.statutGlobal || "—"}</span>
                    </div>
                    <div className="rcp-synthese-kpi">
                        <span className="rcp-kpi-label">Score global</span>
                        <span className="rcp-kpi-value rcp-kpi-value--score">{fiche.scoreGlobal ?? "—"}</span>
                    </div>
                    <div className="rcp-synthese-kpi">
                        <span className="rcp-kpi-label">Décision finale</span>
                        <span className="rcp-kpi-value">{DECISION_LABELS[fiche.decisionFinale] || "En attente"}</span>
                    </div>
                    <div className="rcp-synthese-kpi">
                        <span className="rcp-kpi-label">Seuil atteint</span>
                        <span className="rcp-kpi-value">{fiche.seuilAtteint ? "Oui" : "Non"}</span>
                    </div>
                </div>
                <div className="rcp-summary-box">
                    {fiche.syntheseGlobale || "Aucune synthèse disponible."}
                </div>
            </section>
        </div>
    );
};

export default RecruitmentCandidateEvaluationPage;
